import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Pencil, Plus, Send, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import type { Paginated } from '@barbeiropro/contracts';
import { ConfirmDialog } from '@/components/shared/confirm-dialog';
import { DataTable, type DataColumn } from '@/components/shared/data-table';
import { FormModal } from '@/components/shared/form-modal';
import { PageHeader } from '@/components/shared/page-header';
import { LoadingState } from '@/components/shared/states';
import { useTableParams } from '@/components/shared/use-table-params';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { api, errorMessage } from '@/lib/api';
import { fmtDateTime } from '@/lib/format';
import { useTimezone } from '@/lib/org';
import { useEmailAvailability, type Campaign, type CampaignPreview, type CampaignSendResult } from './api';
import { CampaignFormModal } from './campaign-form';

const STATUS: Record<Campaign['status'], string> = { draft: 'Rascunho', sending: 'Enviando', sent: 'Enviada' };

function SendModal({ campaign, onClose }: { campaign: Campaign | null; onClose: () => void }) {
  const qc = useQueryClient();
  const [result, setResult] = useState<CampaignSendResult | null>(null);
  const preview = useMutation({ mutationFn: (id: string) => api.post<CampaignPreview>(`/campaigns/${id}/preview`) });
  const send = useMutation({
    mutationFn: (id: string) => api.post<CampaignSendResult>(`/campaigns/${id}/send`),
    onSuccess: (r) => {
      setResult(r);
      qc.invalidateQueries({ queryKey: ['campaigns'] });
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  useEffect(() => {
    setResult(null);
    send.reset();
    if (campaign) preview.mutate(campaign.id);
  }, [campaign?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const count = preview.data?.count ?? 0;
  const configured = preview.data?.emailConfigured ?? true;
  return (
    <FormModal
      open={!!campaign}
      onOpenChange={(v) => !v && onClose()}
      size="sm"
      title={result ? 'Resultado do envio' : 'Enviar campanha'}
      description={campaign?.name}
      footer={
        result ? (
          <Button onClick={onClose}>Fechar</Button>
        ) : (
          <>
            <Button variant="outline" onClick={onClose}>
              Voltar
            </Button>
            <Button disabled={preview.isPending || count === 0 || send.isPending} onClick={() => campaign && send.mutate(campaign.id)}>
              <Send aria-hidden /> {send.isPending ? 'Enviando…' : count > 0 ? `Enviar para ${count} cliente${count === 1 ? '' : 's'}` : 'Enviar'}
            </Button>
          </>
        )
      }
    >
      {result ? (
        <div className="grid gap-3 text-sm" role="status">
          {result.emailConfigured ? (
            <p>
              <strong className="tabular">{result.queued}</strong> e-mail(s) entraram na fila de envio. A entrega acontece em segundo plano.
            </p>
          ) : (
            <p className="rounded-lg border border-oxblood/30 bg-oxblood/5 p-3">
              <strong>Nada foi enviado.</strong> O envio de e-mails não está disponível no momento. Fale com o suporte. Os {result.skippedUnconfigured} e-mail(s) foram apenas registrados como
              “não enviados”.
            </p>
          )}
          <ul className="tabular text-muted-foreground">
            <li>Público: {result.recipients}</li>
            <li>Na fila: {result.queued}</li>
            <li>Não enviados (envio indisponível): {result.skippedUnconfigured}</li>
            {result.duplicates > 0 && <li>Já registrados antes: {result.duplicates}</li>}
          </ul>
        </div>
      ) : preview.isPending ? (
        <LoadingState label="Calculando público…" />
      ) : preview.error ? (
        <p role="alert" className="text-sm text-destructive">
          {errorMessage(preview.error)}
        </p>
      ) : (
        <div className="grid gap-3 text-sm">
          <p>
            <span className="font-display text-4xl tabular">{count}</span> cliente{count === 1 ? '' : 's'} receberá{count === 1 ? '' : 'ão'} este e-mail.
          </p>
          <p className="text-muted-foreground">Só entram clientes que aceitaram comunicações, têm e-mail e não foram excluídos. O envio não pode ser desfeito.</p>
          {count === 0 && <p className="text-oxblood-ink">Nenhum cliente corresponde ao público. Ajuste os filtros da campanha.</p>}
          {!configured && (
            <p className="flex gap-2 rounded-lg border border-brass/40 bg-brass/10 p-3">
              <AlertTriangle className="mt-0.5 size-4 shrink-0" aria-hidden /> O envio de e-mails não está disponível no momento. Se continuar, nada será enviado de verdade.
            </p>
          )}
        </div>
      )}
    </FormModal>
  );
}

export function CampaignsPage() {
  const qc = useQueryClient();
  const tz = useTimezone();
  const params = useTableParams();
  const email = useEmailAvailability();
  const [editing, setEditing] = useState<Campaign | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [sending, setSending] = useState<Campaign | null>(null);
  const list = useQuery({
    queryKey: ['campaigns', 'list', params.query],
    queryFn: () => api.get<Paginated<Campaign>>('/campaigns', { page: params.query.page, pageSize: params.query.pageSize, q: params.query.q }),
    placeholderData: (p) => p,
  });
  const remove = async (c: Campaign) => {
    try {
      await api.del(`/campaigns/${c.id}`);
      toast.success('Rascunho excluído');
      qc.invalidateQueries({ queryKey: ['campaigns'] });
    } catch (e) {
      toast.error(errorMessage(e));
      throw e;
    }
  };

  const columns: DataColumn<Campaign>[] = [
    {
      id: 'name',
      header: 'Campanha',
      cell: ({ row }) => (
        <div className="min-w-0">
          <p className="truncate font-medium">{row.original.name}</p>
          <p className="max-w-72 truncate text-xs text-muted-foreground">{row.original.subject}</p>
        </div>
      ),
    },
    { id: 'status', header: 'Situação', cell: ({ row }) => <Badge variant={row.original.status === 'sent' ? 'secondary' : 'outline'}>{STATUS[row.original.status]}</Badge> },
    { id: 'recipients', header: 'Público', meta: { align: 'right' }, cell: ({ row }) => (row.original.status === 'draft' ? '—' : row.original.recipientsCount) },
    {
      id: 'date',
      header: 'Data',
      cell: ({ row }) => <span className="tabular">{fmtDateTime(row.original.sentAt ?? row.original.createdAt, tz)}</span>,
    },
    {
      id: 'actions',
      header: () => <span className="sr-only">Ações</span>,
      meta: { align: 'right' },
      cell: ({ row }) => {
        const c = row.original;
        if (c.status !== 'draft') return null;
        return (
          <div className="flex justify-end gap-1">
            <Button size="sm" onClick={() => setSending(c)}>
              <Send aria-hidden /> Enviar
            </Button>
            <Button variant="ghost" size="icon" aria-label={`Editar ${c.name}`} onClick={() => { setEditing(c); setFormOpen(true); }}>
              <Pencil aria-hidden />
            </Button>
            <ConfirmDialog
              trigger={
                <Button variant="ghost" size="icon" aria-label={`Excluir ${c.name}`}>
                  <Trash2 className="text-destructive" aria-hidden />
                </Button>
              }
              title={`Excluir o rascunho "${c.name}"?`}
              description="Esta ação não pode ser desfeita."
              confirmLabel="Excluir rascunho"
              onConfirm={() => remove(c)}
            />
          </div>
        );
      },
    },
  ];
  const create = (
    <Button onClick={() => { setEditing(null); setFormOpen(true); }}>
      <Plus aria-hidden /> Nova campanha
    </Button>
  );

  return (
    <div>
      <PageHeader title="Campanhas" description="E-mails para clientes que aceitaram receber novidades e promoções." actions={create} />
      {email.data && !email.data.available && (
        <div role="alert" className="mb-4 flex gap-2 rounded-lg border border-brass/40 bg-brass/10 px-4 py-3 text-sm">
          <AlertTriangle className="mt-0.5 size-4 shrink-0" aria-hidden />
          <p>
            <strong>O envio de e-mails não está disponível no momento.</strong> Você pode criar campanhas e ver o público, mas nenhum e-mail será enviado de verdade. Fale com o suporte.
          </p>
        </div>
      )}
      <DataTable
        columns={columns}
        data={list.data}
        params={params}
        isLoading={list.isLoading}
        error={list.error}
        onRetry={() => list.refetch()}
        searchPlaceholder="Buscar campanha"
        empty={{ title: 'Nenhuma campanha ainda', description: 'Crie uma campanha para chamar de volta clientes que sumiram.', action: create }}
      />
      <CampaignFormModal open={formOpen} onOpenChange={setFormOpen} campaign={editing} />
      <SendModal campaign={sending} onClose={() => setSending(null)} />
    </div>
  );
}
